import type { RunStatus } from "@/lib/enums";

/**
 * Presentation logic for scores, bands, durations and deltas.
 *
 * Pure and unit-tested, because these are the judgements the whole app is read
 * through — what counts as "keep as written" versus "rework", and whether a
 * missing number reads as zero or as unknown. Getting the second one wrong is
 * how a failed run starts looking like a terrible plan.
 */

/** What the reader should DO about a score. This is the Guide's own rubric. */
type ScoreBand = "absent" | "rework" | "patch" | "keep";

/**
 * 0 is absent — the section is missing from the plan entirely, which is a
 * different fact from scoring 1. It gets its own band so the UI can say
 * "absent" rather than "rework", and so it is never confused with a low score
 * that at least has something written to work with.
 */
export function scoreBand(score: number | null): ScoreBand | null {
  if (score === null) return null;
  if (score === 0) return "absent";
  if (score >= 7) return "keep";
  if (score >= 4) return "patch";
  return "rework";
}

/** The legend under the scorecard header, in rubric order. */
export const BAND_LEGEND: readonly { band: ScoreBand; label: string }[] = [
  { band: "rework", label: "1–3 rework" },
  { band: "patch", label: "4–6 patch" },
  { band: "keep", label: "7–10 keep" },
];

/**
 * Token-backed colour per band. Returns CSS custom property NAMES, not hex, so a
 * brand edit in design-system/tokens.css flows through — nothing here hard-codes
 * a colour. `absent` shares danger with `rework`: both are red, and the
 * distinction is carried by the label, not the colour.
 */
export function bandColorVar(band: ScoreBand | null): string {
  switch (band) {
    case "keep":
      return "var(--success)";
    case "patch":
      return "var(--warning)";
    case "rework":
    case "absent":
      return "var(--danger)";
    default:
      return "var(--fg-3)";
  }
}

export function bandSurfaceVar(band: ScoreBand | null): string {
  switch (band) {
    case "keep":
      return "var(--success-bg)";
    case "patch":
      return "var(--warning-bg)";
    case "rework":
    case "absent":
      return "var(--danger-bg)";
    default:
      return "var(--bg-3)";
  }
}

export function bandTextVar(band: ScoreBand | null): string {
  switch (band) {
    case "keep":
      return "var(--success-fg)";
    case "patch":
      return "var(--warning-fg)";
    case "rework":
    case "absent":
      return "var(--danger-fg)";
    default:
      return "var(--fg-2)";
  }
}

/** An em dash, not "0" or "—0.0". A missing score is unknown, never zero. */
export const NO_VALUE = "—";

export function formatScore(score: number | null): string {
  return score === null ? NO_VALUE : score.toFixed(1);
}

/**
 * Bar width for a 0–10 score. An absent section (0) gets no bar at all, which is
 * the point: the row still shows, with an empty track, so the gap is visible.
 *
 * No minimum width. Section scores are integers, so the smallest real bar is
 * 1 → 10%, already clearly visible; a floor would be dead code dressed up as a
 * legibility guard.
 */
export function scoreBarWidth(score: number | null): string {
  if (score === null || score === 0) return "0%";
  return `${score * 10}%`;
}

/** Signed, with a true minus sign. Null stays an em dash — no "+0.0" for unknown. */
export function formatDelta(delta: number | null): string {
  if (delta === null) return NO_VALUE;
  if (delta === 0) return "0.0";
  const sign = delta > 0 ? "+" : "−";
  return `${sign}${Math.abs(delta).toFixed(1)}`;
}

/**
 * Whether a delta is good news. Deliberately a separate call from formatting:
 * for scores, up is good; for flags per plan, DOWN is good. The caller says
 * which, because the number itself cannot.
 */
export function deltaToneVar(delta: number | null, upIsGood = true): string {
  if (delta === null || delta === 0) return "var(--fg-3)";
  const good = upIsGood ? delta > 0 : delta < 0;
  return good ? "var(--success)" : "var(--danger)";
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return NO_VALUE;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${String(rest).padStart(2, "0")}s`;
}

/** "Aug 31, 2026". Locale-fixed so seeded demo data reads consistently. */
export function formatDate(iso: string | null): string {
  if (iso === null) return NO_VALUE;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NO_VALUE;
  return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

/** "Aug 31" — for dense rows where the year is obvious from context. */
export function formatShortDate(iso: string | null): string {
  if (iso === null) return NO_VALUE;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NO_VALUE;
  return date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

/** "2026-08" → "Aug". Parsed as UTC so a timezone cannot shift the month. */
export function formatMonth(month: string): string {
  const [year, monthPart] = month.split("-");
  if (year === undefined || monthPart === undefined) return month;
  const date = new Date(Date.UTC(Number(year), Number(monthPart) - 1, 1));
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
}

/** "4m ago" / "3 days ago" — for a run that is still going. */
export function formatElapsed(iso: string | null): string {
  if (iso === null) return NO_VALUE;
  const started = new Date(iso).getTime();
  if (Number.isNaN(started)) return NO_VALUE;
  const seconds = Math.max(0, Math.round((Date.now() - started) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * A run status's colour. `failed` is red and `running` is blue — deliberately NOT
 * the score bands, because a run's health and a plan's quality are different
 * questions and sharing a palette between them invites reading a failed run as a
 * bad plan.
 */
/**
 * What a solution ROW reports, which is not quite a run status.
 *
 * `reaudit-due` is the one value with no run behind it: the run completed
 * perfectly, and it is the plan that needs attention because the Guide moved on
 * beneath it. Keeping it out of `RUN_STATUSES` is deliberate — a run's own
 * history must stay a record of what the run did, not of what happened after.
 */
export type RowStatus = RunStatus | "reaudit-due";

export const ROW_STATUS_LABELS: Record<RowStatus, string> = {
  queued: "Queued",
  running: "Running",
  complete: "Complete",
  failed: "Failed",
  "reaudit-due": "Re-audit due",
};

/**
 * A solution row's status, most urgent first.
 *
 * A run in flight or a failed run outranks a stale score: both say something is
 * happening (or has just gone wrong) right now, whereas a superseded standard is
 * a standing condition. `reauditDue` is only ever computed from a scored run, so
 * it cannot fire on a solution that has never been audited.
 */
export function rowStatus(status: RunStatus, reauditDue: boolean): RowStatus {
  if (status === "running" || status === "queued" || status === "failed") return status;
  return reauditDue ? "reaudit-due" : status;
}

export function statusColorVars(status: RowStatus): { surface: string; text: string; dot: string } {
  switch (status) {
    case "reaudit-due":
      // Amber: the score is not wrong, it is out of date. Red would read as a
      // failure and green as a clean bill of health; both would be a lie.
      return { surface: "var(--warning-bg)", text: "var(--warning-fg)", dot: "var(--warning)" };
    case "complete":
      return { surface: "var(--success-bg)", text: "var(--success-fg)", dot: "var(--success)" };
    case "running":
      return { surface: "var(--info-bg)", text: "var(--info-fg)", dot: "var(--info)" };
    case "failed":
      return { surface: "var(--danger-bg)", text: "var(--danger-fg)", dot: "var(--danger)" };
    default:
      return { surface: "var(--bg-3)", text: "var(--fg-2)", dot: "var(--fg-3)" };
  }
}

/** "2/4" — how much of what the Guide asks for the section actually carries. */
export function formatFields(present: number, required: number): string {
  return required === 0 ? NO_VALUE : `${present}/${required}`;
}

// ── Solution slugs ───────────────────────────────────────────────────────────

/** Longest slug we will mint. The column is `text`; this is for readability. */
const MAX_SLUG_LENGTH = 60;

/**
 * Latin letters NFKD does NOT decompose, because they are distinct letters
 * rather than accented ones — so stripping combining marks leaves them intact
 * and the a-z filter then deletes them outright. "Ærø" became "r".
 *
 * Losing a letter from a client's name is silent and permanent (the slug is the
 * URL), so they are transliterated the way their own languages do it.
 */
const LETTER_TRANSLITERATIONS: readonly [RegExp, string][] = [
  [/æ/g, "ae"],
  [/œ/g, "oe"],
  [/ø/g, "o"],
  [/ß/g, "ss"],
  [/đ|ð/g, "d"],
  [/ł/g, "l"],
  [/þ/g, "th"],
];

/**
 * A URL-safe identity for a solution name — "Nymbl · Plan Audit" →
 * "nymbl-plan-audit".
 *
 * Falls back to "solution" rather than returning an empty string: a name of
 * only emoji or only punctuation is possible, and an empty slug would collide
 * with itself and produce an unreachable URL. The unique index on
 * `solution.slug` is still the real guarantee — see `pickAvailableSlug`.
 */
export function slugifySolutionName(name: string): string {
  const folded = name
    .normalize("NFKD")
    // Strip combining marks so "Zeppelín" and "Zeppelin" agree.
    .replace(/\p{M}/gu, "")
    .toLowerCase();
  const transliterated = LETTER_TRANSLITERATIONS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    folded,
  );
  const slug = transliterated
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, "");
  return slug.length > 0 ? slug : "solution";
}

/**
 * The first free slug in the `base`, `base-2`, `base-3` … series.
 *
 * Suffixes from 2, not 1, because "acme-2" reads as the second Acme while
 * "acme-1" implies there is no plain "acme". `taken` is whatever the caller read
 * from the database a moment ago, so this is a courtesy, not a guarantee — two
 * simultaneous creates race it and the loser hits the unique index. The caller
 * handles that; see `createAuditFromUpload`.
 */
export function pickAvailableSlug(base: string, taken: readonly string[]): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
}

// ── The Solutions row's "latest run" sentence ────────────────────────────────

/**
 * Only what this sentence needs, so it can be tested without a whole run.
 * Structural, not a copy of `SolutionRow["latestRun"]` — that carries a
 * reference and a failure reason this does not read.
 */
interface LatestRunSummary {
  status: RunStatus;
  startedAt: string | null;
  durationSeconds: number | null;
}

/**
 * The latest run as one sentence, per the design's "Latest run" column.
 *
 * `queued` is its own branch, and that is the point: a queued run has never
 * started, so folding it in with `running` printed "Running · started —" for
 * something that was not running and had no start time. That was unreachable
 * while nothing created runs, and became reachable the moment uploads did.
 */
export function latestRunText(run: LatestRunSummary | null): string {
  if (run === null) return "Never audited";
  if (run.status === "queued") return "Queued · not started";
  if (run.status === "running") return `Running · started ${formatElapsed(run.startedAt)}`;
  if (run.status === "failed") {
    return `${formatDate(run.startedAt)} · failed at ${formatDuration(run.durationSeconds)}`;
  }
  return `${formatDate(run.startedAt)} · ${formatDuration(run.durationSeconds)}`;
}

/**
 * "84.9 MB" / "142 KB" / "38 B" — a file size a person reads.
 *
 * Shared rather than per-surface, because the same wording appears in the upload
 * picker AND in the `intakeSummary` the server stores ("3 files · 142 KB"). Two
 * copies would drift, and the stored string is what the detail screen shows
 * forever.
 *
 * KB/MB in the binary sense the file pickers of every OS use. Not worth a
 * pedantic KiB here — the audience is reading "is this the right file", not
 * auditing storage.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
